import { useState, useCallback } from 'react';

export interface UseDialogStateReturn<T = unknown> {
  /** Whether the dialog is currently open */
  isOpen: boolean;
  /** Data associated with the open dialog */
  data: T | null;
  /** Open the dialog, optionally with associated data */
  open: (data?: T) => void;
  /** Close the dialog and clear data */
  close: () => void;
}

/**
 * Hook for managing dialog open/close state with optional associated data
 *
 * @example
 * const confirmDialog = useDialogState<Voter>();
 *
 * // Open with data
 * confirmDialog.open(selectedVoter);
 *
 * // In JSX
 * <Dialog open={confirmDialog.isOpen} onClose={confirmDialog.close}>
 *   <DialogContent>Delete {confirmDialog.data?.name}?</DialogContent>
 * </Dialog>
 */
export function useDialogState<T = unknown>(): UseDialogStateReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);

  const open = useCallback((newData?: T) => {
    setData(newData ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  return {
    isOpen,
    data,
    open,
    close,
  };
}

export default useDialogState;
